import { apiClient } from './apiClient';
import type { BaseCategoryItem, ImportResult } from '@/types/categories';
import {
  mockDepartmentCategories,
  mockTaskCategories,
  mockPriorities,
  mockLabels,
  mockPrintTemplates,
  mockRoleCategories,
} from '@/mocks/categories';

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true' || !import.meta.env.VITE_API_BASE_URL;

function delay<T>(v: T, ms = 200): Promise<T> {
  return new Promise((r) => setTimeout(() => r(v), ms));
}

// In-memory stores cho mock mode (để thay đổi có hiệu lực khi CRUD)
const stores: Record<string, BaseCategoryItem[]> = {
  departments: [...mockDepartmentCategories],
  'task-categories': [...mockTaskCategories],
  priorities: [...mockPriorities],
  labels: [...mockLabels],
  'print-templates': [...mockPrintTemplates],
  roles: [...mockRoleCategories],
};

export function createCategoryService<T extends BaseCategoryItem>(resource: string) {
  // Xóa key rỗng "" để backend không fail parse Guid?
  const clean = (obj: any) => {
    const out: any = {};
    for (const [k, v] of Object.entries(obj ?? {})) {
      if (v === '' || v === undefined) continue;
      out[k] = v;
    }
    return out;
  };

  return {
    async list(): Promise<T[]> {
      if (USE_MOCK) return delay((stores[resource] ?? []) as T[]);
      const { data } = await apiClient.get<T[]>(`/categories/${resource}`);
      return data;
    },

    async create(payload: Partial<T>): Promise<T> {
      if (USE_MOCK) {
        const now = new Date().toISOString();
        const newItem = {
          ...payload,
          id: `${resource}-${Date.now()}`,
          sortOrder: payload.sortOrder ?? (stores[resource]?.length ?? 0) + 1,
          isActive: payload.isActive ?? true,
          createdAt: now,
          updatedAt: now,
        } as unknown as T;
        stores[resource] = [...(stores[resource] ?? []), newItem];
        return delay(newItem);
      }
      const { data } = await apiClient.post<T>(`/categories/${resource}`, clean(payload));
      return data;
    },

    async update(id: string, payload: Partial<T>): Promise<T> {
      if (USE_MOCK) {
        const list = stores[resource] ?? [];
        const idx = list.findIndex((x) => x.id === id);
        if (idx < 0) throw new Error('Không tìm thấy');
        const updated = { ...list[idx], ...payload, updatedAt: new Date().toISOString() } as T;
        list[idx] = updated;
        return delay(updated);
      }
      const { data } = await apiClient.patch<T>(`/categories/${resource}/${id}`, clean(payload));
      return data;
    },

    async delete(id: string): Promise<void> {
      if (USE_MOCK) {
        const list = stores[resource] ?? [];
        const item = list.find((x) => x.id === id);
        if (item?.isSystem) throw new Error('Không thể xóa mục hệ thống');
        stores[resource] = list.filter((x) => x.id !== id);
        return delay(undefined);
      }
      await apiClient.delete(`/categories/${resource}/${id}`);
    },

    async import(file: File): Promise<ImportResult> {
      if (USE_MOCK) {
        // Giả lập parse Excel
        await delay(undefined, 800);
        const mockTotal = 12;
        const mockSuccess = 10;
        const mockFailed = 2;
        return {
          total: mockTotal,
          success: mockSuccess,
          failed: mockFailed,
          errors: [
            { row: 3, code: 'DUP_CODE', message: 'Mã trùng với mục đã có (không phải update)' },
            { row: 7, message: 'Thiếu trường bắt buộc: name' },
          ],
        };
      }
      const form = new FormData();
      form.append('file', file);
      const { data } = await apiClient.post<ImportResult>(
        `/categories/${resource}/import`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return data;
    },

    exportUrl(): string {
      return `${import.meta.env.VITE_API_BASE_URL ?? '/api'}/categories/${resource}/export`;
    },
  };
}

// Export concrete services
export const departmentService = createCategoryService('departments');
export const taskCategoryService = createCategoryService('task-categories');
export const priorityService = createCategoryService('priorities');
export const labelService = createCategoryService('labels');
export const printTemplateService = createCategoryService('print-templates');
export const roleService = createCategoryService('roles');
